/* eslint-disable react/no-unescaped-entities */
import react, { useEffect } from 'react'
import FieldDescription from './field-description'
import { ReportField } from '@/reports/reports'

export interface ReportOptionsProps {
  checked: ReportField[]
  sessionId: string
}

const ReportOptions = (props: ReportOptionsProps) => {
  const [saved, setSaved] = react.useState(false)
  const [selected, setSelected] = react.useState<ReportField[]>(props.checked)
  const [error, setError] = react.useState('')

  async function handleReportOptions() {
    console.log('selected', selected)
    
    // Save the data
		const resp = await fetch(`/api/report`, {
			method: 'put',
			headers: {
				Session: props.sessionId,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(selected)
		})

    // Handle errors
    if (!resp.ok) {
      console.error('Failed to save report fields', resp)
      setError('Error saving data')
      return
    }

    // Parse response
    const json = await resp.json()
    console.info('saved report fields', json)

    // Briefly updated the text on the save button
    setSaved(true)

    setTimeout(() => {
      setSaved(false)
    }, 4000)

    setError('')
		return json.success
  }

  // Add or remove values from the selected
  // report options list
  function selectHandler(value: ReportField) {
    return () => {
      if (!selected) {
        setSelected([value])
      } else if (selected?.includes(value)) {
        setSelected(selected.filter((v) => v !== value))
      } else {
        setSelected([...selected, value])
      }
    }
  }

  useEffect(() => {
    console.log('updating state', props)
    setSelected(props.checked)
  }, [props.checked])

  return (
    <section className='border-b border-gray-300 my-5 pb-5 bg-slate-200 p-8'>
      <p className='text-sm italic mt-0'>
        Either Date or Month must be selected as a report dimension.
      </p>
      <FieldDescription 
        header='Date'
        description='The date associated with each metric.'
        googleFormula='segments.date'
        bingFormula='date_start'
        facebookFormula='TimePeriod'
        checked={selected?.includes('date')}
        selectHandler={selectHandler('date')}
      />
      <FieldDescription 
        header='Month'
        description='The month associated with each metric.'
        googleFormula='segments.month'
        bingFormula='TimePeriod'
        facebookFormula='date_start'
        checked={selected?.includes('month')}
        selectHandler={selectHandler('month')}
      />
      <FieldDescription 
        header='Campaign Id'
        description='The campaign id associated with each metric.'
        googleFormula='campaign.id'
        bingFormula='CampaignId'
        facebookFormula='campaign_id'
        checked={selected?.includes('campaignId')}
        selectHandler={selectHandler('campaignId')}
      />
      <FieldDescription 
        header='Campaign Name'
        description='The name of your campaign.'
        googleFormula='campaign.name'
        bingFormula='CampaignName'
        facebookFormula='campaign_name'
        checked={selected?.includes('campaignName')}
        selectHandler={selectHandler('campaignName')}
      />
      <FieldDescription 
        header='Impressions'
        description='The number of times your ad was shown.'
        googleFormula='impressions'
        bingFormula='Impressions'
        facebookFormula='impressions'
        checked={selected?.includes('impressions')}
        selectHandler={selectHandler('impressions')}
      />
      <FieldDescription 
        header='Clicks'
        description='The number of times your ad was clicked.'
        googleFormula='clicks'
        bingFormula='Clicks'
        facebookFormula='action => action_type = link_click'
        checked={selected?.includes('clicks')}
        selectHandler={selectHandler('clicks')}
      />
      <FieldDescription 
        header='Click Through Rate (CTR)'
        description='Click Through Rate (CTR) is the ratio of clicks to impressions.'
        googleFormula='ctr'
        bingFormula='Ctr'
        facebookFormula='ctr'
        checked={selected?.includes('ctr')}
        selectHandler={selectHandler('ctr')}
      />
      <FieldDescription 
        header='Average Cost Per Click (CPC)'
        description='Average Cost Per Click (CPC) is the average amount you pay for each click.'
        googleFormula='averageCpc / 1000000'
        bingFormula='AverageCpc'
        facebookFormula='cpc'
        checked={selected?.includes('cpc')}
        selectHandler={selectHandler('cpc')}
      />
      <FieldDescription 
        header='Cost'
        description='The amount you spent on advertising.'
        googleFormula='costmicros / 1000000'
        bingFormula='Spend'
        facebookFormula='cost'
        checked={selected?.includes('cost')}
        selectHandler={selectHandler('cost')}
      />
      <FieldDescription 
        header='Conversions'
        description='The number of conversions.'
        googleFormula='allConversions'
        bingFormula='Conversions'
        facebookFormula='action_values => action_type = purchase'
        checked={selected?.includes('conversions')}
        selectHandler={selectHandler('conversions')}
      />
      <FieldDescription 
        header='Conversion Rate (CVR)'
        description='Conversion Rate is the ratio of conversions to clicks.'
        googleFormula='allConversionsFromInteractionsRate'
        bingFormula='Conversions / Clicks'
        facebookFormula="actions => (action_values => action_type === 'purchase') / (actions => action_type === 'link_click')"
        checked={selected?.includes('cvr')}
        selectHandler={selectHandler('cvr')}
      />
      <FieldDescription 
        header='Cost Per Conversion (CPC)'
        description='Cost Per Conversion is the average amount you pay for each conversion.'
        googleFormula='costPerConversion / 1000000'
        bingFormula='CostPerConversion'
        facebookFormula='spend / ( action_values => action_type === purchase )'
        checked={selected?.includes('costPerConversion')}
        selectHandler={selectHandler('costPerConversion')}
      />
      <FieldDescription 
        header='Revenue'
        description='The amount of revenue generated.'
        googleFormula='allConversionsValue'
        bingFormula='Revenue'
        facebookFormula="action_values => action_type === 'purchase'"
        checked={selected?.includes('revenue')}
        selectHandler={selectHandler('revenue')}
      />
      <FieldDescription 
        header='Return on Ad Spend (ROAS)'
        description='Return on Ad Spend (ROAS) is a measures the revenue generated for every dollar spent on advertising.'
        googleFormula='allconversionsvalue / (costmicros / 1000)'
        bingFormula='revenue / spend'
        facebookFormula='revenue / cost'
        checked={selected?.includes('roas')}
        selectHandler={selectHandler('roas')}
      />
              
      <button 
        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 ml-5 rounded"
        onClick={() => handleReportOptions()}
      >
      { saved ? ' ✔️ ' : 'Save'}

      <div className='block text-red-600'>{ error }</div>
    </button>
    </section>
  )
}

export default ReportOptions